/**
 * Feed (glossary): a per-person queue of fans worth messaging now (plan §6,
 * docs/plans/workspace-r3-handoff.md). The CRM builds pages of up to 200 fans;
 * this module holds the person's Feed settings and the queue rules the widget
 * follows: the first 10 cards never move, the rest can be re-merged, more is
 * fetched when 100 are left, and a fan who just had a chat leaves at once.
 */

export type FeedSourceId = 'spenders' | 'lists';
export type RankId = 'spent' | 'last_purchase';
export type SpendPeriod = 'all' | '90d' | '30d' | '7d';
export type ChatPeriod = 'today' | '24h' | '3d' | '7d';

export interface FeedSettings {
  sources: { id: FeedSourceId; on: boolean }[];
  minSpend: number;
  spendPeriod: SpendPeriod;
  chatPeriod: ChatPeriod;
  minBp: number;
  minTemp: number;
  includeUnknown: boolean;
  rankBy: { id: RankId; on: boolean }[];
  spread: boolean;
  /** Seconds between cards in the Card view; 0 = off. */
  autoAdvance: number;
  snoozeHours: number;
  /** Attached OnlyFans lists per Account: {accountId: [listId]}. */
  lists: Record<string, string[]>;
  /** Names of the attached lists, for the "why suggested" line. */
  listNames: Record<string, string>;
}

export const SPEND_PERIODS: SpendPeriod[] = ['all', '90d', '30d', '7d'];
export const CHAT_PERIODS: ChatPeriod[] = ['today', '24h', '3d', '7d'];
export const AUTO_ADVANCE: number[] = [0, 2, 5, 10, 15];
export const SNOOZE_HOURS: number[] = [1, 4, 12, 24];
export const FROZEN = 10;
/** The first request asks for this many fans so the list shows at once; a refill brings the rest. */
export const FIRST_BATCH = 20;
export const REFILL_AT = 100;
export const RESOLVE_AHEAD = 30;
export const LIST_MAX_MEMBERS = 5000;

export const DEFAULT_FEED: FeedSettings = {
  sources: [
    { id: 'spenders', on: true },
    { id: 'lists', on: true },
  ],
  minSpend: 1,
  spendPeriod: 'all',
  chatPeriod: 'today',
  minBp: 0,
  minTemp: 0,
  includeUnknown: true,
  rankBy: [
    { id: 'spent', on: true },
    { id: 'last_purchase', on: true },
  ],
  spread: true,
  autoAdvance: 5,
  snoozeHours: 4,
  lists: {},
  listNames: {},
};

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function ordered<T extends string>(raw: unknown, ids: readonly T[], fallback: { id: T; on: boolean }[]): { id: T; on: boolean }[] {
  const out: { id: T; on: boolean }[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const id = (isObject(item) ? item.id : item) as T;
    if (ids.includes(id) && !out.some((x) => x.id === id)) out.push({ id, on: !(isObject(item) && item.on === false) });
  }
  for (const f of fallback) if (!out.some((x) => x.id === f.id)) out.push(f);
  return out;
}

function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function num(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

/** The person's saved Feed settings (settings.feed), repaired. */
export function feedSettingsFrom(raw: unknown): FeedSettings {
  const r = isObject(raw) ? raw : {};
  const lists: Record<string, string[]> = {};
  if (isObject(r.lists)) {
    for (const [accountId, ids] of Object.entries(r.lists)) {
      const clean = (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
      if (clean.length) lists[accountId] = [...new Set(clean)];
    }
  }
  const listNames: Record<string, string> = {};
  if (isObject(r.listNames)) for (const [k, v] of Object.entries(r.listNames)) if (typeof v === 'string') listNames[k] = v;
  return {
    sources: ordered(r.sources, ['spenders', 'lists'] as const, DEFAULT_FEED.sources),
    minSpend: num(r.minSpend, 0, 1_000_000, DEFAULT_FEED.minSpend),
    spendPeriod: pick(r.spendPeriod, SPEND_PERIODS, DEFAULT_FEED.spendPeriod),
    chatPeriod: pick(r.chatPeriod, CHAT_PERIODS, DEFAULT_FEED.chatPeriod),
    minBp: Math.round(num(r.minBp, 0, 5, 0)),
    minTemp: Math.round(num(r.minTemp, 0, 4, 0)),
    includeUnknown: r.includeUnknown !== false,
    rankBy: ordered(r.rankBy, ['spent', 'last_purchase'] as const, DEFAULT_FEED.rankBy),
    spread: r.spread !== false,
    autoAdvance: pick(Number(r.autoAdvance), AUTO_ADVANCE, DEFAULT_FEED.autoAdvance),
    snoozeHours: pick(Number(r.snoozeHours), SNOOZE_HOURS, DEFAULT_FEED.snoozeHours),
    lists,
    listNames,
  };
}

/** Start of the "no chat in" period: local midnight for Today, else a rolling window. */
export function chatSince(period: ChatPeriod, nowMs: number): string {
  if (period === 'today') {
    const d = new Date(nowMs);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  const hours = period === '24h' ? 24 : period === '3d' ? 72 : 168;
  return new Date(nowMs - hours * 3600_000).toISOString();
}

/** What the CRM's /feed endpoint reads (the server validates it again). */
export function requestSettings(s: FeedSettings) {
  return {
    sources: s.sources,
    minSpend: s.minSpend,
    spendPeriod: s.spendPeriod,
    minBp: s.minBp,
    minTemp: s.minTemp,
    includeUnknown: s.includeUnknown,
    rankBy: s.rankBy,
    spread: s.spread,
    lists: s.lists,
  };
}

/** Settings that change which fans the server picks (a change rebuilds the queue). */
export function queryKey(s: FeedSettings, scope: string[] | 'all'): string {
  return JSON.stringify([requestSettings(s), s.chatPeriod, scope]);
}

export interface FeedItem {
  account_id: string;
  fan_id: string;
  source: FeedSourceId;
  fan_name: string | null;
  fan_username: string | null;
  fan_avatar: string | null;
  spent: number;
  last_purchase_at: string | null;
  last_purchase_amount: number | null;
  last_chat_at: string | null;
  last_chat_by: 'fan' | 'team' | 'ai' | null;
  bp: number | null;
  temp: number | null;
  list_ids: string[];
  pending: boolean;
}

export interface FeedPage {
  items: FeedItem[];
  has_more: boolean;
  lists: { account_id: string; list_id: string; status: string; member_count: number | null }[];
}

export function feedKey(i: { account_id: string; fan_id: string }): string {
  return `${i.account_id}:${i.fan_id}`;
}

export interface FeedQueue {
  /** Upcoming fans in order; `pending` ones wait for their scores. */
  items: FeedItem[];
  /** Fans passed this session (Next / Skip / chatted): never suggested again here. */
  seen: string[];
  hasMore: boolean;
}

export const EMPTY_QUEUE: FeedQueue = { items: [], seen: [], hasMore: true };

/** The cards shown: fans whose scores are known (or not needed). */
export function shown(q: FeedQueue): FeedItem[] {
  return q.items.filter((i) => !i.pending);
}

/** Keys of the first FROZEN shown cards: they never move or gain neighbours in front. */
export function frozenKeys(q: FeedQueue): string[] {
  return shown(q).slice(0, FROZEN).map(feedKey);
}

/** What a refill request must leave out: everything passed or already queued. */
export function excludeForAppend(q: FeedQueue): string[] {
  return [...q.seen, ...q.items.map(feedKey)];
}

/** What a re-merge request leaves out: passed fans and the frozen head (the rest is replaced). */
export function excludeForMerge(q: FeedQueue): string[] {
  return [...q.seen, ...frozenKeys(q)];
}

/** A refill page goes after everything already queued. */
export function appendPage(q: FeedQueue, page: FeedPage): FeedQueue {
  const have = new Set([...q.seen, ...q.items.map(feedKey)]);
  return { ...q, items: [...q.items, ...page.items.filter((i) => !have.has(feedKey(i)))], hasMore: page.has_more };
}

/**
 * A fresh ranking replaces everything after the frozen head (new list members,
 * new scores, fans who stopped qualifying). The head stays exactly as it was.
 */
export function mergePage(q: FeedQueue, page: FeedPage): FeedQueue {
  const head = new Set(frozenKeys(q));
  const kept = q.items.filter((i) => head.has(feedKey(i)));
  const seen = new Set(q.seen);
  const rest = page.items.filter((i) => !head.has(feedKey(i)) && !seen.has(feedKey(i)));
  return { ...q, items: [...kept, ...rest], hasMore: page.has_more };
}

/** A new ranking from scratch (settings or scope changed): only `seen` survives. */
export function resetWith(q: FeedQueue, page: FeedPage): FeedQueue {
  const seen = new Set(q.seen);
  return { seen: q.seen, items: page.items.filter((i) => !seen.has(feedKey(i))), hasMore: page.has_more };
}

/**
 * Coming back to a remembered queue: its order stays, but fans the fresh ranking
 * no longer has (chatted since, snoozed elsewhere, stopped qualifying) leave, and
 * the fresh ranking fills in after what is kept. Nothing is inserted above a
 * remembered row.
 */
export function restorePage(q: FeedQueue, page: FeedPage): FeedQueue {
  const fresh = new Map(page.items.map((i) => [feedKey(i), i]));
  const seen = new Set(q.seen);
  const kept: FeedItem[] = [];
  for (const it of q.items) {
    const now = fresh.get(feedKey(it));
    if (now && !seen.has(feedKey(it))) kept.push(it.pending && !now.pending ? now : it);
  }
  const have = new Set(kept.map(feedKey));
  const rest = page.items.filter((i) => !have.has(feedKey(i)) && !seen.has(feedKey(i)));
  return { ...q, items: [...kept, ...rest], hasMore: page.has_more };
}

/** A fan leaves the queue (chatted, skipped, passed); passed/skipped also go to `seen`. */
export function removeFan(q: FeedQueue, key: string, markSeen = true): FeedQueue {
  if (!q.items.some((i) => feedKey(i) === key) && (!markSeen || q.seen.includes(key))) return q;
  return { ...q, items: q.items.filter((i) => feedKey(i) !== key), seen: markSeen && !q.seen.includes(key) ? [...q.seen, key] : q.seen };
}

/** Next: the current card is passed. */
export function nextCard(q: FeedQueue): FeedQueue {
  const cur = shown(q)[0];
  return cur ? removeFan(q, feedKey(cur)) : q;
}

/** Back: the last passed fan returns to the front (if we still have it). */
export function previousCard(q: FeedQueue, passed: FeedItem[]): { queue: FeedQueue; passed: FeedItem[] } {
  const last = passed[passed.length - 1];
  if (!last) return { queue: q, passed };
  const key = feedKey(last);
  return {
    queue: { ...q, items: [last, ...q.items.filter((i) => feedKey(i) !== key)], seen: q.seen.filter((k) => k !== key) },
    passed: passed.slice(0, -1),
  };
}

/** Applies score verdicts from /feed/resolve: passing fans show, failing ones leave. */
export function applyVerdicts(q: FeedQueue, verdicts: { account_id: string; fan_id: string; bp: number | null; temp: number | null; verdict: string }[]): FeedQueue {
  const byKey = new Map(verdicts.map((v) => [feedKey(v), v]));
  const items: FeedItem[] = [];
  for (const it of q.items) {
    const v = byKey.get(feedKey(it));
    if (!v) {
      items.push(it);
      continue;
    }
    if (v.verdict === 'fail') continue;
    items.push({ ...it, bp: v.bp, temp: v.temp, pending: v.verdict === 'pending' });
  }
  return { ...q, items };
}

/** Pending fans among the next RESOLVE_AHEAD positions (their scores are needed soon). */
export function pendingAhead(q: FeedQueue): FeedItem[] {
  return q.items.slice(0, RESOLVE_AHEAD).filter((i) => i.pending);
}

export function needsRefill(q: FeedQueue): boolean {
  return q.hasMore && shown(q).length <= REFILL_AT;
}

/** "$1,280". */
export function money(n: number | null | undefined): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: v < 100 ? 2 : 0 })}`;
}
